// eslint-disable-next-line @altermaind/you-shall-not-pass
import { useCallback, FC, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, useThemeValue, IconButton, Svg, Asset, Button } from '@altermaind/web/ui';
import { ClaimsComponent } from '@/_shared/components/Claims';
import { Roles } from '@/_shared/constants/roles';
import { Routes } from '@/_shared/constants/routes';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useBreakingPoints, useResponsiveStyle, useDialog, useNotification } from '@/_shared/hooks';
import { ContentType } from '@/swagger/constants/contents-orchestrator-enums';
import { useFavorite } from '@/training/contentsLikes/hooks/useFavorite';
import {
  getContentDetailsCall,
  getContentStartCall,
  postContentStartCall,
} from '@/training/hardSkills/state';
import { StartVideoHeaderSkeleton } from './StartVideoHeaderSkeleton';
import { StartVideoSkeleton } from './StartVideoSkeleton';
import { DialogStartVideoPayload } from './types';

dayjs.extend(duration);

export const DialogStartVideo: FC = () => {
  const t = useTranslations('video.startDialog');
  const tGeneric = useTranslations('trainingHardSkills.cards.GENERIC');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { closeDialog, Dialog, isOpen, payload } =
    useDialog<DialogStartVideoPayload>('videoStarter');
  const navigate = useNavigate();

  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();
  const gapM = convertSizeToNumber(`${useThemeValue('sizes', 'm')}`);
  const primaryColor = useThemeValue('colors', 'primary');
  const primaryBackgroundColor = useThemeValue('colors', 'primary/background');
  const {
    execute: executeGetContentDetails,
    loading: loadingGetContentDetails,
    error: errorGetContentDetails,
    data,
  } = getContentDetailsCall.hook();

  const { execute: executeGetContentStart, loading: loadingGetContentStart } =
    getContentStartCall.hook();

  const { execute: executePostContentStart, loading: loadingPostContentStart } =
    postContentStartCall.hook();

  const videoId = useMemo(() => payload?.videoId || '', [payload?.videoId]);
  const isSkillToBoost = useMemo(() => payload?.isSkillToBoost || false, [payload?.isSkillToBoost]);

  const {
    isFavorite: localIsFavorite,
    isLoading: isFavoriteLoading,
    toggleFavorite: onToggleFavorite,
  } = useFavorite({ contentId: videoId, initialIsFavorite: data?.userDetails?.favorite });

  const isVideoInProgress = useMemo(
    () =>
      (data?.userDetails?.progress?.startedAt && !data?.userDetails?.progress?.completedAt) || '',
    [data?.userDetails?.progress?.completedAt, data?.userDetails?.progress?.startedAt],
  );

  const isVideoCompleted = useMemo(
    () => !!data?.userDetails?.progress?.completedAt,
    [data?.userDetails?.progress?.completedAt],
  );

  const textButton = useMemo(() => {
    if (isVideoInProgress) {
      return t('resume');
    }
    if (isVideoCompleted) {
      return t('restart');
    }
    return t('start');
  }, [isVideoInProgress, isVideoCompleted, t]);

  const iconButton = useMemo(() => {
    if (isVideoInProgress) {
      return 'emptyPlay';
    }
    if (isVideoCompleted) {
      return 'refresh';
    }
    return 'emptyPlay';
  }, [isVideoInProgress, isVideoCompleted]);

  const executeToUse = useMemo(
    () =>
      isVideoInProgress || isVideoCompleted ? executeGetContentStart : executePostContentStart,
    [executeGetContentStart, executePostContentStart, isVideoCompleted, isVideoInProgress],
  );

  const loadingToUse = useMemo(
    () =>
      isVideoInProgress || isVideoCompleted ? loadingGetContentStart : loadingPostContentStart,
    [isVideoCompleted, isVideoInProgress, loadingGetContentStart, loadingPostContentStart],
  );

  const VideoInfoContent = useMemo(() => {
    return loadingGetContentDetails ? (
      <StartVideoHeaderSkeleton />
    ) : (
      <Box
        display='flex'
        mx='l'
        flexDirection='column'
        px='s'
        width='100%'
        zIndex={99}
        justifyContent='flex-end'
        height='100%'
      >
        <Box mb='xs' textAlign='left'>
          <Typography variant='p2/light'>
            {data?.tags?.map(tag => tag.label.toLocaleUpperCase()).join(', ')}
          </Typography>
          <Typography variant='h1'>{data?.title}</Typography>
        </Box>
        <Box
          display='flex'
          flexWrap='wrap'
          justifyContent={genericResponsiveValue({
            breakPoints: { desktop: 'center' },
            defaultValue: 'left',
          })}
          alignItems='stretch'
          style={{ gap: gapM }}
          marginRight={genericResponsiveValue({
            breakPoints: { mobile: '0' },
            defaultValue: 'auto',
          })}
          mb='l'
        >
          {/* DURATION */}
          <Box display='flex' flexDirection='row' alignItems='center'>
            <Svg name='emptyPlay' fill='primary/text' size='xs' />
            <Typography variant='p2' ml='xxs' color='primary/text'>
              {t('duration', {
                time: dayjs.duration(data?.durationMinutes ?? 0, 'minutes').asMinutes(),
              })}
            </Typography>
          </Box>

          {/* RATING (se presenti) */}
          {data?.averageRating ? (
            <Box display='flex' flexDirection='row' alignItems='center'>
              <Svg name='star' stroke='primary/text' size='s' />
              <Typography variant='p2' ml='xxs' color='primary/text'>
                {data.averageRating.toFixed(1)}
              </Typography>
            </Box>
          ) : null}

          {/* CREDITS (se presenti) */}
          {data?.credits ? (
            <Box display='flex' flexDirection='row' alignItems='center'>
              <Svg name='wallet' stroke='primary/text' size='xs' />
              <Typography variant='p2' color='primary/text' ml='xxs'>
                {t('credits', { credits: data?.credits })}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }, [
    loadingGetContentDetails,
    data?.tags,
    data?.title,
    data?.durationMinutes,
    data?.averageRating,
    data?.credits,
    gapM,
    genericResponsiveValue,
    t,
  ]);

  const Header = useMemo(() => {
    return (
      <Box
        p='xs'
        display='flex'
        borderRadius='m'
        borderBottomLeftRadius={0}
        borderBottomRightRadius={0}
        alignItems='center'
        flexDirection='row'
        padding='none'
        pt='s'
        height={250}
        background={`linear-gradient(
          to bottom,
          ${primaryColor},
          transparent
        ),
        conic-gradient(from 0.725turn at 90% 35%, ${primaryColor} 1rad, ${primaryBackgroundColor} 4rad)`}
        position='relative'
      >
        {isDesktop ? VideoInfoContent : null}
        <Box
          style={{ mixBlendMode: 'luminosity' }}
          marginLeft='auto'
          marginRight='xxl'
          marginTop='auto'
          display='flex'
        >
          <Asset width={225} domain='images' src={data?.thumbnailUrl} />
        </Box>
        <Box
          position='absolute'
          left={0}
          bottom={0}
          width='100%'
          height='100%'
          backgroundColor='primary'
          opacity={0.3}
        />
      </Box>
    );
  }, [primaryColor, primaryBackgroundColor, isDesktop, VideoInfoContent, data?.thumbnailUrl]);

  const navigateToVideo = useCallback(
    (
      videoResponse:
        | ReturnType<typeof getContentStartCall.hook>['response']
        | ReturnType<typeof postContentStartCall.hook>['response'],
    ) => {
      if (videoResponse?.contentPath) {
        const startFrom =
          videoResponse?.progress?.type === ContentType.VIDEO
            ? videoResponse?.progress?.seconds || undefined
            : undefined;

        closeDialog();
        navigate(Routes.Video.replace(':videoId', videoId), {
          state: {
            videoSrc: videoResponse?.contentPath,
            startFrom,
          },
        });
      }
    },
    [closeDialog, navigate, videoId],
  );

  const buttonClickHandler = useCallback(() => {
    executeToUse({
      contentId: videoId,
      successSaga: ({ response }) => {
        // Navigate to video route when API call succeeds
        navigateToVideo(response);
      },
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
  }, [executeToUse, navigateToVideo, notify, tShared, videoId]);

  useEffect(() => {
    if (videoId) {
      executeGetContentDetails({
        contentId: videoId,
        errorSaga: () => {
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeGetContentDetails, tShared, videoId]);

  return (
    <Dialog
      header={Header}
      headerStyle={{ mt: 'none', mb: 'none' }}
      open={isOpen}
      title={undefined}
      onClose={closeDialog}
      closeButton
      specialsComponentsInHeader={
        <>
          {/* Skill to boost badge - top left */}
          {!loadingGetContentDetails &&
            isSkillToBoost &&
            !isVideoInProgress &&
            !isVideoCompleted && (
              <Box
                ml={genericResponsiveValue({
                  breakPoints: {
                    desktop: 'm',
                    tablet: 'xxs',
                  },
                  defaultValue: 'none',
                })}
                px='xxs'
                py='xxxs'
                borderRadius='xl'
                alignItems='center'
                display='flex'
                flexDirection='row'
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
              >
                <Svg fill='white' name='rocket' size='xs' />
                <Typography color='white' variant='p3/light' ml='xxxs' fontSize='xxs'>
                  {tGeneric('skillToBoost')}
                </Typography>
              </Box>
            )}
          {/* Favorite button - top right */}
          <Box display='flex' flex={1} width='100%' justifyContent='flex-end' mr='xxs'>
            {!loadingGetContentDetails ? (
              <ClaimsComponent roles={[Roles.Employee, Roles.Manager]}>
                <Button
                  onClick={onToggleFavorite}
                  variant='positive'
                  spinnerColor='primary/text'
                  padding='none'
                  minWidth='auto'
                  size='m'
                  loading={isFavoriteLoading}
                  disabled={isFavoriteLoading}
                >
                  <Svg
                    name={localIsFavorite ? 'bookmark' : 'emptyBookmark'}
                    size='xs'
                    fill='primary'
                  />
                </Button>
              </ClaimsComponent>
            ) : null}
          </Box>
        </>
      }
    >
      {!isDesktop ? VideoInfoContent : null}
      <Box display='flex' mx='l' flexDirection='column' px='s' width='100%'>
        {loadingGetContentDetails ? (
          <StartVideoSkeleton />
        ) : (
          <>
            <Typography variant='p2/light'>{data?.description}</Typography>
            {/* line between row rowIndex and rowIndex+1 (not after the last one) */}
            {data && data?.views > 0 && (
              <Typography variant='p3/light' color='primary/text' mt='m'>
                {t(`colleaguesHint.${data?.views === 1 ? 'singular' : 'plural'}`, {
                  colleagues: data?.views || 0,
                })}
              </Typography>
            )}
          </>
        )}

        <IconButton
          variant='primary'
          onClick={buttonClickHandler}
          mt='l'
          mr='auto'
          data-testid='confirm-operation-button'
          loading={loadingToUse || loadingGetContentDetails}
          disabled={loadingToUse || loadingGetContentDetails || !!errorGetContentDetails}
          prefixSvg={iconButton}
          svgStyle={{
            size: 'xs',
          }}
          textStyle={{
            paddingLeft: 'xxs',
          }}
          spinnerColor='white'
        >
          {textButton}
        </IconButton>
      </Box>
    </Dialog>
  );
};
